/* global axios */
/* eslint-disable max-classes-per-file */
import ApiClient from './ApiClient';

// Account-level API client for fetching all follow-ups
class AccountFollowUpsAPI extends ApiClient {
  constructor() {
    super('follow-ups', { accountScoped: true });
  }

  getAll({ page = 1 } = {}) {
    return axios.get(this.url, {
      params: { page },
    });
  }

  // NOOVI: CSV import de follow-ups (Gap FU-E — fase-11)
  importFollowUps(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

// Conversation-level API client for CRUD operations
class ConversationFollowUpsAPI extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  // Normalize a date/string into ISO-8601 (UTC) so the backend always receives
  // an unambiguous instant. Audit 2026-05-30 (TZ-03): shared by create + update.
  // eslint-disable-next-line class-methods-use-this
  normalizeScheduledAt(value) {
    if (!value) return value;
    return value instanceof Date
      ? value.toISOString()
      : new Date(value).toISOString();
  }

  create(conversationId, data) {
    const payload = {
      follow_up: {
        content: data.message || data.content,
        scheduled_at: this.normalizeScheduledAt(
          data.scheduledAt || data.scheduled_at
        ),
        title: data.title,
        inbox_id: data.inboxId || data.inbox_id,
        conversation_id: conversationId,
        follow_up_template_id: data.templateId || data.template_id || null,
        // Meta-approved WhatsApp template (Cloud inbox): name/language/namespace
        // + processed_params variables. Null for text/internal-template follow-ups.
        template_params: data.templateParams || data.template_params || null,
      },
    };
    return axios.post(`${this.url}/${conversationId}/follow-ups`, payload);
  }

  get({ conversationId, page = 1 } = {}) {
    return axios.get(`${this.url}/${conversationId}/follow-ups`, {
      params: { page },
    });
  }

  show(conversationId, id) {
    return axios.get(`${this.url}/${conversationId}/follow-ups/${id}`);
  }

  update(conversationId, id, data) {
    return axios.patch(`${this.url}/${conversationId}/follow-ups/${id}`, {
      follow_up: {
        content: data.message || data.content,
        scheduled_at: this.normalizeScheduledAt(
          data.scheduledAt || data.scheduled_at
        ),
        title: data.title,
        inbox_id: data.inboxId || data.inbox_id,
      },
    });
  }

  delete(conversationId, id) {
    return axios.delete(`${this.url}/${conversationId}/follow-ups/${id}`);
  }

  // Cancel a pending follow-up (marks it cancelled + removes the Sidekiq job).
  cancel(conversationId, id) {
    return axios.post(`${this.url}/${conversationId}/follow-ups/${id}/cancel`);
  }

  // Retry a failed follow-up (reschedules it and clears item idempotency claims).
  retrySend(conversationId, id) {
    return axios.post(
      `${this.url}/${conversationId}/follow-ups/${id}/retry_send`
    );
  }

  getCount(conversationId) {
    return axios.get(`${this.url}/${conversationId}/follow-ups/count`);
  }
}

// Combined API with both account-level and conversation-level methods
class FollowUpsAPI {
  constructor() {
    this.accountAPI = new AccountFollowUpsAPI();
    this.conversationAPI = new ConversationFollowUpsAPI();
  }

  // Delegate to appropriate API client
  getAll(params) {
    return this.accountAPI.getAll(params);
  }

  // NOOVI: CSV import de follow-ups (Gap FU-E — fase-11)
  importFollowUps(file) {
    return this.accountAPI.importFollowUps(file);
  }

  create(conversationId, data) {
    return this.conversationAPI.create(conversationId, data);
  }

  get(params) {
    return this.conversationAPI.get(params);
  }

  show(conversationId, id) {
    return this.conversationAPI.show(conversationId, id);
  }

  update(conversationId, id, data) {
    return this.conversationAPI.update(conversationId, id, data);
  }

  delete(conversationId, id) {
    return this.conversationAPI.delete(conversationId, id);
  }

  cancel(conversationId, id) {
    return this.conversationAPI.cancel(conversationId, id);
  }

  retrySend(conversationId, id) {
    return this.conversationAPI.retrySend(conversationId, id);
  }

  getCount(conversationId) {
    return this.conversationAPI.getCount(conversationId);
  }
}

export default new FollowUpsAPI();
